// The Me sidebar: the parts' nav from the top (no name above it), favorites, then the
// parts' sections (labels), and at the foot what goes on (the call one is in).
// Nav rows kept apart (`NavItem.apart`: the cloud's DM) follow the others
// between two lines, above the favorites (the person, 2026-11-01).
// Beside a rail (cloud, or this PC signed in to it) the account is at the
// rail's foot. Without one (the offline edition on its own, where no one else
// sees its owner, so no name shows) the settings are at the sidebar's foot.

import { m } from "@trameria/i18n";
import type { ReactNode } from "react";
import { useMeApp } from "../app/context";
import { FavoritesSection } from "../me/favorites-section";
import { BrandMark, Icon, iconOf } from "../ui/icon";
import { MenuButton, type MenuItem } from "../ui/menu";
import { Tooltip } from "../ui/tooltip";
import { APART_RULE, ICON_ROW } from "./collapsed-sidebar";
import { useShellFrame } from "./frame";
import { NavRow } from "./nav-row";
import { OngoingRows } from "./ongoing";
import type { ShellMenuItem } from "./outside/contract";
import {
  entryIcon,
  menuItems,
  useShellMenu,
  type WebEntryExtras,
} from "./outside/source";

const notAway = () => false;
const SettingsIcon = iconOf("settings");

export function MeSidebar() {
  const app = useMeApp();
  const menu = useShellMenu();
  const frame = useShellFrame();
  // Me lives elsewhere: its sections have nothing to show here.
  const meAway = app.extensions.meAway;
  const away = (meAway?.useSectionsAway ?? meAway?.useAway ?? notAway)();
  const items = menuItems(menu.menu, "me");
  const isApart = (item: ShellMenuItem) => !!menu.extras.get(item.id)?.apart;
  const apart = items.filter(isApart);
  return (
    <nav
      aria-label={m.shell_me()}
      className="flex h-full min-h-0 flex-col bg-sidebar pt-2.5"
    >
      {/* No row above the nav (the person, 2026-11-01): the first row starts
          where the folded column's first icon does (`CollapsedColumn` with no
          head). */}
      <div className="mt-1 min-h-0 flex-1 overflow-y-auto px-2 pb-4">
        <div className="flex flex-col gap-px">
          {items
            .filter((item) => !isApart(item))
            .map((item, i) => {
              const row = (
                <NavItemRow
                  key={item.id}
                  item={item}
                  extras={menu.extras.get(item.id)}
                />
              );
              // Room for the lock the frame lays over the first row's end.
              return i === 0 && frame.lockInRow ? (
                <div key={item.id} className="flex items-center gap-0.5">
                  <div className="min-w-0 flex-1">{row}</div>
                  <div aria-hidden="true" className="w-9 flex-none" />
                </div>
              ) : (
                row
              );
            })}
          {/* In the column of the rows, as in the folded one (APART_RULE). */}
          {apart.length > 0 && (
            <>
              <div aria-hidden="true" className={`${APART_RULE} mx-2`} />
              {apart.map((item) => (
                <NavItemRow
                  key={item.id}
                  item={item}
                  extras={menu.extras.get(item.id)}
                />
              ))}
              <div aria-hidden="true" className={`${APART_RULE} mx-2`} />
            </>
          )}
        </div>
        {/* Not in the folded column: fades in as the sidebar grows out of it. */}
        {!away && (
          <div data-fold-label="">
            <FavoritesSection place="me" />
            {app.navSections.map((s, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: sections are fixed for the app's life
              <s.Component key={i} />
            ))}
          </div>
        )}
      </div>
      <OngoingRows
        shape="row"
        className={`px-2 ${menu.menu.rail ? "pb-2.5" : ""}`}
      />
      {!menu.menu.rail && (
        <div className="flex flex-col px-2 pt-1 pb-2.5">
          <NavRow
            to="/me/settings"
            icon={SettingsIcon}
            label={m.shell_settings()}
          />
        </div>
      )}
    </nav>
  );
}

/**
 * The first row of a sidebar: what the sidebar is about (the account in Me,
 * the workspace in a workspace) with its menu. One shape for both sidebars.
 * Where the frame puts the lock (`SidebarLock`) beside it (no window controls'
 * strip above), the row leaves its end free for it. `folded`: the mark alone,
 * for the top of the folded column.
 */
export function SidebarSwitcher({
  label,
  mark,
  name,
  items,
  footer,
  folded = false,
}: {
  /** Read aloud for the menu button. */
  label: string;
  /** The picture at the left (an avatar or a workspace's tile, 22 px). */
  mark: ReactNode;
  name: string;
  items: readonly (MenuItem | "separator")[];
  /** Under the menu's items (the account's theme and language). */
  footer?: ReactNode;
  folded?: boolean;
}) {
  const frame = useShellFrame();
  if (folded)
    return (
      <Tooltip text={label} side="right">
        <MenuButton
          label={label}
          className={ICON_ROW}
          items={items}
          footer={footer}
        >
          {mark}
        </MenuButton>
      </Tooltip>
    );
  return (
    <div className="flex items-center gap-0.5">
      {/* 7 px at the left: the mark lands where the folded column centers it
          ((52 - 22) / 2 = 15 = 8 + 7), so it stays put as the sidebar grows
          out of the column or folds into it. */}
      <MenuButton
        label={label}
        className="flex h-9 min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-[7px] pr-2 pl-[7px] text-left text-[13.5px] text-sidebar-fg-strong hover:bg-sidebar-hover"
        items={items}
        footer={footer}
      >
        {mark}
        <span data-fold-label="" className="min-w-0 flex-1 truncate">
          {name}
        </span>
        <span data-fold-label="" className="flex">
          <Icon kind="updown" className="text-sidebar-muted" size={14} />
        </span>
      </MenuButton>
      {/* Room for the lock the frame lays over this end. */}
      {frame.lockInRow && <div aria-hidden="true" className="w-9 flex-none" />}
    </div>
  );
}

/**
 * Keeps the sidebar open, shown over the panel from the folded column (a peek
 * or the drawer). The frame places it at the drawer's top right; open in the
 * row there is none (`]` or the edge folds it).
 */
export function SidebarLock() {
  const frame = useShellFrame();
  const label = m.shell_keep_sidebar_open();
  return (
    <Tooltip text={`${label}  ]`} side="right">
      <button
        type="button"
        aria-label={label}
        data-sidebar-lock=""
        className={ICON_ROW}
        onClick={() => frame.setCollapsed(false)}
      >
        {/* Open: the sidebar is not kept yet; pressing keeps it (the person,
            2026-10-30). */}
        <Icon kind="unlock" className="pointer-events-none" />
      </button>
    </Tooltip>
  );
}

/**
 * The trameria mark in the folded column's window-controls strip (offline
 * desktop app). Decorative, not a button: resting on the strip peeks like the
 * rest of the column, so a button there could hardly be pressed.
 */
export function SidebarStripMark() {
  const frame = useShellFrame();
  return (
    <div
      data-sidebar-strip-mark=""
      data-fold-only=""
      className="flex size-8 items-center justify-center"
      onPointerEnter={(e) => frame.peek.start(e.pointerType)}
      onPointerLeave={frame.peek.cancel}
    >
      <BrandMark size={22} />
    </div>
  );
}

function NavItemRow({
  item,
  extras,
}: {
  item: ShellMenuItem;
  extras: WebEntryExtras | undefined;
}) {
  const badge = extras?.useBadge?.();
  return (
    <NavRow
      to={item.to ?? "/me"}
      icon={entryIcon(item, extras)}
      label={item.label}
      end={badge}
    />
  );
}
